"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.benchmarkSetSchema = void 0;
exports.parseBenchmarksDocument = parseBenchmarksDocument;
// `config/benchmarks/<set>.yaml` (SPEC §14.3, CR-001 §10.3): decile ranges per measure and collection
// type. The benchmarks document holds every file under `config/benchmarks/` as a named set keyed by its
// file stem, and each file's issues are reported against that file (`benchmarks/<set>`). A decile is
// `null` where CMS prints `--`; overlap is checked over the non-null deciles only.
const zod_1 = require("zod");
const config_issue_js_1 = require("./config-issue.js");
const measures_js_1 = require("./measures.js");
// The value list of `ref.benchmark`'s CHECK constraint (packages/schema/src/db/aco-org.ts).
const BENCHMARK_TYPES = ["HISTORICAL", "PERFORMANCE_PERIOD", "FLAT", "SYNTHETIC"];
// CMS benchmarks always have ten deciles; `ref.benchmark_decile` checks `decile BETWEEN 1 AND 10`.
const DECILE_COUNT = 10;
const rateSchema = zod_1.z.number().min(0).max(100);
const decileSchema = zod_1.z
    .strictObject({
    decile: zod_1.z.int().min(1).max(DECILE_COUNT),
    low: rateSchema.nullable(),
    high: rateSchema.nullable(),
})
    .superRefine(({ low, high }, ctx) => {
    if ((low === null) !== (high === null)) {
        const message = "low and high are both numbers or both null (CMS prints --)";
        ctx.addIssue({ code: "custom", path: [], message });
    }
    else if (low !== null && high !== null && low > high) {
        ctx.addIssue({ code: "custom", path: [], message: `low ${low} is above high ${high}` });
    }
});
const isBounded = (d) => d.low !== null && d.high !== null;
const describeDecile = (d) => `decile ${d.decile} (${d.low}–${d.high})`;
function checkDecileOrder(deciles, ctx) {
    deciles.forEach((d, index) => {
        if (d.decile !== index + 1) {
            const message = `deciles run 1 to ${DECILE_COUNT} in order; expected ${index + 1}`;
            ctx.addIssue({ code: "custom", path: ["deciles", index, "decile"], message });
        }
    });
}
function checkDecileOverlap(deciles, ctx) {
    const earlier = [];
    deciles.forEach((d, index) => {
        if (!isBounded(d))
            return;
        const clash = earlier.find((e) => d.low <= e.high && e.low <= d.high);
        if (clash) {
            const message = `${describeDecile(d)} overlaps ${describeDecile(clash)}`;
            ctx.addIssue({ code: "custom", path: ["deciles", index], message });
        }
        earlier.push(d);
    });
}
const benchmarkSchema = zod_1.z
    .strictObject({
    measure_number: measures_js_1.measureNumberSchema,
    collection_type: measures_js_1.collectionTypeSchema,
    benchmark_type: zod_1.z.enum(BENCHMARK_TYPES),
    is_inverse: zod_1.z.boolean(),
    is_topped_out: zod_1.z.boolean(),
    seven_point_cap: zod_1.z.boolean(),
    average_rate: rateSchema.nullable(),
    deciles: zod_1.z.array(decileSchema).length(DECILE_COUNT),
})
    .superRefine((benchmark, ctx) => {
    checkDecileOrder(benchmark.deciles, ctx);
    checkDecileOverlap(benchmark.deciles, ctx);
});
function checkDuplicateMeasures(benchmarks, ctx) {
    const seen = new Map();
    benchmarks.forEach((benchmark, index) => {
        const key = `${benchmark.measure_number} (${benchmark.collection_type})`;
        const first = seen.get(key);
        if (first === undefined)
            seen.set(key, index);
        else {
            const message = `measure ${key} is already in benchmarks[${first}]`;
            ctx.addIssue({ code: "custom", path: ["benchmarks", index, "measure_number"], message });
        }
    });
}
exports.benchmarkSetSchema = zod_1.z
    .strictObject({
    name: zod_1.z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "name is lower-case kebab-case"),
    performance_year: zod_1.z.int().min(1000).max(9999),
    source: zod_1.z.string().min(1),
    benchmarks: zod_1.z.array(benchmarkSchema),
})
    .superRefine((set, ctx) => checkDuplicateMeasures(set.benchmarks, ctx));
const isPlainObject = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
/** Parses the benchmarks document; each set's issues name the document `benchmarks/<file stem>`. */
function parseBenchmarksDocument(input) {
    if (!isPlainObject(input)) {
        const message = "the benchmarks document is a map of set name to benchmark set";
        return { success: false, issues: [{ document: "benchmarks", path: "", message }] };
    }
    const data = {};
    const issues = [];
    for (const [stem, value] of Object.entries(input)) {
        const document = `benchmarks/${stem}`;
        const result = (0, config_issue_js_1.parseConfigDocument)(exports.benchmarkSetSchema, document, value);
        if (!result.success)
            issues.push(...result.issues);
        else if (result.data.name !== stem) {
            const message = `name ${result.data.name} must equal the file stem ${stem}`;
            issues.push({ document, path: "name", message });
        }
        else
            data[stem] = result.data;
    }
    return issues.length > 0 ? { success: false, issues } : { success: true, data };
}
//# sourceMappingURL=benchmarks.js.map